import type { OrganizationSummary } from "@lms/contracts";
import {
  AppShell,
  Button,
  PageHeader,
  PlaceholderPage,
  StatCard,
  StatusPill
} from "@lms/ui";
import { Route, Routes, useLocation } from "react-router-dom";

const navigation = [
  { label: "Overview", href: "/" },
  { label: "Organizations", href: "/organizations", marker: "18" },
  { label: "Domains", href: "/domains" },
  { label: "Feature access", href: "/features" },
  { label: "Operations", href: "/operations" },
  { label: "Audit events", href: "/audit" }
];

const organizations: OrganizationSummary[] = [
  { id: "org-1", name: "Northstar Nursing Institute", domain: "northstar.luma.test", members: 212, plan: "Pilot", status: "Active" },
  { id: "org-2", name: "Cedar Health College", domain: "cedar.luma.test", members: 86, plan: "Standard", status: "Trial" },
  { id: "org-3", name: "Mercy Skills Academy", domain: "learn.mercyskills.example", members: 341, plan: "Enterprise", status: "Active" }
];

function Dashboard() {
  return (
    <>
      <PageHeader
        eyebrow="Internal operations"
        title="Platform control center"
        description="Onboard organizations, manage domains, and monitor platform-wide operations."
        actions={<><Button variant="secondary">Review alerts</Button><Button>Create organization</Button></>}
      />

      <section className="stats-grid" aria-label="Platform summary">
        <StatCard label="Organizations" value="18" detail="14 active · 4 trials" trend="+3" />
        <StatCard label="Licensed seats" value="2,480" detail="1,936 currently assigned" />
        <StatCard label="Custom domains" value="7" detail="1 pending verification" />
        <StatCard label="Job health" value="99.9%" detail="Last 24 hours" trend="Healthy" />
      </section>

      <section className="content-grid">
        <div className="panel">
          <div className="panel-header"><h2>Recently active organizations</h2><a href="/organizations">View all</a></div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Organization</th><th>Members</th><th>Plan</th><th>Status</th></tr></thead>
              <tbody>
                {organizations.map((organization) => (
                  <tr key={organization.id}>
                    <td><strong>{organization.name}</strong><small>{organization.domain}</small></td>
                    <td>{organization.members}</td>
                    <td>{organization.plan}</td>
                    <td><StatusPill tone={organization.status === "Active" ? "success" : "warning"}>{organization.status}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="panel">
          <div className="panel-header"><h2>Operational feed</h2></div>
          <div className="activity-list">
            <div className="activity-item"><span className="activity-icon">D</span><span><strong>Domain verified</strong><small>learn.mercyskills.example</small></span><span className="activity-time">12m</span></div>
            <div className="activity-item"><span className="activity-icon">O</span><span><strong>Organization created</strong><small>Cedar Health College</small></span><span className="activity-time">2h</span></div>
            <div className="activity-item"><span className="activity-icon">J</span><span><strong>Import job completed</strong><small>184 learner records</small></span><span className="activity-time">5h</span></div>
          </div>
        </aside>
      </section>
    </>
  );
}

export function App() {
  const location = useLocation();

  return (
    <AppShell
      appName="Platform console"
      workspaceName="Luma Operations"
      navigation={navigation}
      activeHref={location.pathname}
      user={{ name: "Avery Stone", role: "Platform administrator" }}
      accent="#b16b3f"
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/organizations" element={<PlaceholderPage title="Organizations" description="Organization lifecycle, owners, seats, and feature access will be managed here." />} />
        <Route path="/domains" element={<PlaceholderPage title="Custom domains" description="DNS validation, SSL state, and hostname mappings will be visible here." />} />
        <Route path="/features" element={<PlaceholderPage title="Feature access" description="Plan entitlements and time-limited overrides will be managed here." />} />
        <Route path="/operations" element={<PlaceholderPage title="Operations" description="Job failures, retries, imports, and system health will live here." />} />
        <Route path="/audit" element={<PlaceholderPage title="Platform audit events" description="Every sensitive internal action will be searchable from this page." />} />
      </Routes>
    </AppShell>
  );
}

